import { useParams, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Shield, Database, AlertCircle, Users } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";

import PageBreadcrumbs from "@/components/PageBreadcrumbs";
import CityHero from "@/components/stadt/CityHero";
import CityFAQ from "@/components/stadt/CityFAQ";
import CityTestimonials from "@/components/stadt/CityTestimonials";
import CityCrossLinks from "@/components/stadt/CityCrossLinks";
import AnimatedStatsBar from "@/components/stadt/AnimatedStatsBar";
import InteractiveRiskMap from "@/components/stadt/InteractiveRiskMap";
import RiskCalculatorHybrid from "@/components/stadt/RiskCalculatorHybrid";
import PracticeTypesShowcase from "@/components/stadt/PracticeTypesShowcase";
import ConditionsAccordion from "@/components/stadt/ConditionsAccordion";
import CaseStudiesCarousel from "@/components/stadt/CaseStudiesCarousel";
import FloatingContactWidget from "@/components/FloatingContactWidget";
import CTASection from "@/components/CTASection";
import StickyCTA from "@/components/tarif/StickyCTA";
import ExpertSection from "@/components/ExpertSection";
import StructuredData from "@/components/StructuredData";
import { cityData } from "@/utils/cityData";
const CyberversicherungStadt = () => {
  const {
    city: citySlug
  } = useParams<{
    city: string;
  }>();
  if (!citySlug || !cityData[citySlug]) {
    return <Navigate to="/cyberversicherung" replace />;
  }
  const city = cityData[citySlug];

  // Strukturierte Daten für SEO
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [{
      "@type": "ListItem",
      "position": 1,
      "name": "Home",
      "item": "https://praxis-versichert.de/"
    }, {
      "@type": "ListItem",
      "position": 2,
      "name": "Cyberversicherung",
      "item": "https://praxis-versichert.de/cyberversicherung"
    }, {
      "@type": "ListItem",
      "position": 3,
      "name": city.name,
      "item": `https://praxis-versichert.de/cyberversicherung/${city.slug}`
    }]
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": `Praxis Versichert - Cyberversicherung ${city.name}`,
    "image": "https://praxis-versichert.de/logo.png",
    "url": `https://praxis-versichert.de/cyberversicherung/${city.slug}`,
    "telephone": "+4917670790268",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": city.name,
      "addressRegion": city.state,
      "addressCountry": "DE"
    },
    "areaServed": {
      "@type": "City",
      "name": city.name,
      "containedInPlace": {
        "@type": "State",
        "name": city.state
      }
    },
    "priceRange": "€€",
    "description": `Professionelle Cyberversicherung für Arztpraxen, MVZ und Kliniken in ${city.name}. Schutz vor Ransomware, Datenverlust und DSGVO-Bußgeldern.`,
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": city.stats.practiceCount
    }
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Cyberversicherung für Arztpraxen",
    "provider": {
      "@type": "Organization",
      "name": "Praxis Versichert"
    },
    "areaServed": city.name,
    "description": `Professionelle Cyberversicherung für medizinische Einrichtungen in ${city.name}. 24/7 Schutz, schnelle Schadenregulierung, kostenlose Beratung.`,
    "offers": {
      "@type": "Offer",
      "priceCurrency": "EUR",
      "price": "29.00",
      "priceSpecification": {
        "@type": "UnitPriceSpecification",
        "price": "29.00",
        "priceCurrency": "EUR",
        "unitText": "MONTH"
      }
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": city.faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  const aggregateRatingSchema: Record<string, any> = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": `Cyberversicherung ${city.name}`,
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": city.stats.practiceCount,
      "bestRating": "5",
      "worstRating": "1"
    },
    "offers": {
      "@type": "AggregateOffer",
      "priceCurrency": "EUR",
      "lowPrice": "49",
      "highPrice": "299",
      "offerCount": "12"
    }
  };
  return <>
      <Helmet>
        <title>Cyberversicherung {city.name} | Schutz für Arztpraxen 2025</title>
        <meta name="description" content={`Cyberversicherung für Ärzte in ${city.name}. 24/7 Schutz für Praxen, MVZ & Kliniken. Jetzt kostenlos beraten lassen! ☎ Expertenberatung`} />
        <meta name="keywords" content={`Cyberversicherung ${city.name}, Arztpraxis Cyberschutz ${city.name}, Praxis Versicherung ${city.name}, DSGVO Versicherung ${city.name}`} />
        <link rel="canonical" href={`https://praxis-versichert.de/cyberversicherung/${city.slug}`} />
        
        {/* Open Graph */}
        <meta property="og:title" content={`Cyberversicherung ${city.name} für Arztpraxen`} />
        <meta property="og:description" content={`Professioneller Cyberschutz für medizinische Einrichtungen in ${city.name}. Jetzt beraten lassen!`} />
        <meta property="og:url" content={`https://praxis-versichert.de/cyberversicherung/${city.slug}`} />
        <meta property="og:type" content="website" />
      </Helmet>

      <StructuredData data={localBusinessSchema} />
      <StructuredData data={serviceSchema} />
      <StructuredData data={faqSchema} />
      <StructuredData data={breadcrumbSchema} />
      <StructuredData data={aggregateRatingSchema} />

      <div className="min-h-screen relative overflow-hidden">
        
        <div className="relative z-10">
          <Navigation />
          <PageBreadcrumbs items={[
            { label: "Home", path: "/" },
            { label: "Cyberversicherung", path: "/cyberversicherung" },
            { label: city.name }
          ]} />
          
          <CityHero city={city} />
            
            <AnimatedStatsBar practiceCount={parseInt(city.stats.practiceCount.replace(/[^\d]/g, ''))} cityName={city.name} />
            
            <InteractiveRiskMap city={city} />
            
            <div id="tariff-calculator">
              <RiskCalculatorHybrid city={city} />
            </div>
            
            <PracticeTypesShowcase city={city} />
            
            <ConditionsAccordion />

            <CaseStudiesCarousel />

            <CityTestimonials city={city} />

            <CityFAQ city={city} />

            <CityCrossLinks currentCity={city} />

            <CTASection cityName={city.name} localTrustFactor={city.stats.practiceCount.toString()} />
            
            <ExpertSection />

            <StickyCTA companyName={city.name} companyColor="hsl(var(--accent))" cityName={city.name} practiceCount={parseInt(city.stats.practiceCount.replace(/[^\d]/g, ''))} />
            
            <FloatingContactWidget cityName={city.name} />
          
          <Footer />
        </div>
      </div>
    </>;
};
export default CyberversicherungStadt;